import { DatePipe } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { Component, DestroyRef, ViewEncapsulation, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import {
    faCamera,
    faChevronDown,
    faChevronRight,
    faPaperPlane,
    faSave,
    faSpinner,
    faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import imageCompression, { Options } from 'browser-image-compression'
import { Observable, forkJoin, of } from 'rxjs'

import {
    FeatureType,
    ListItem,
    resolveCanonicalListValue,
    VI_DAMAGE_QUESTION,
    VI_LEGACY_DAMAGE_QUESTION,
    VehicleInspection,
    ViChecklistItem,
} from '../../shared'
import { RepositoryModal } from '../../shared/modals/base/repository-modal'
import { InspectableVehicle, VehicleInspectionListComponent } from './vehicle-inspection-list.component'

interface PendingPhoto {
    file: File
    url: string
    type: string
}

interface PreviousDamageItem {
    response: string
    comments: string
}

interface PreviousDamageResponse {
    inspectionDate: string | null
    inspectionId: string | null
    damage: PreviousDamageItem | null
    smoke: PreviousDamageItem | null
    newSmoke: PreviousDamageItem | null
}

@Component({
    templateUrl: './vehicle-inspection-modal.component.html',
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    imports: [FormsModule, FaIconComponent, WjInputModule, DatePipe],
    styles: [
        `
            .vi-modal-footer {
                margin-top: 0 !important;
            }
        `,
    ],
})
export class VehicleInspectionModalComponent extends RepositoryModal<VehicleInspection> {
    private destroyRef = inject(DestroyRef)

    faSpinner = faSpinner
    faChevronDown = faChevronDown
    faChevronRight = faChevronRight
    faPaperPlane = faPaperPlane
    faSave = faSave
    faCamera = faCamera
    faTrash = faTrash

    vehicleMap: ListItem<string>[] = []
    vehicleList: InspectableVehicle[] = []
    userMap: ListItem<number>[] = []

    collapsedSections: boolean[] = []
    previousDamage: PreviousDamageResponse | null = null
    previousDamageLoading = false

    private submitOnSave = false
    private expandedItemIds = new Set<string>()
    private pendingUploadsByItemId = new Map<string, PendingPhoto[]>()
    private deletedExistingByItemId = new Map<string, Set<string>>()
    private dragOverItemId: string | null = null

    protected onInitialized() {
        const parent = this.editOptions.initializeParams as VehicleInspectionListComponent
        this.vehicleMap = parent.vehicleMap
        this.vehicleList = parent.vehicleList
        this.userMap = parent.userMap

        const canonicalCode = resolveCanonicalListValue(this.target.vehicleEquipmentCode, this.vehicleMap.map(v => v.value))
        if (canonicalCode != null) {
            this.target.vehicleEquipmentCode = canonicalCode
        }

        this.collapsedSections = this.target.sections.map(() => false)

        if (this.isAdding) {
            const currentUserId = this.services.authentication.hrref
            if (currentUserId && this.userMap.some(u => u.value === currentUserId)) {
                this.target.inspectorHrRef = currentUserId
                const match = this.userMap.find(u => u.value === currentUserId)
                if (match) this.target.inspectorName = match.display
            }
        }

        const focusId = parent.focusInspectionItemId
        if (focusId) {
            queueMicrotask(() => this.focusChecklistItemById(focusId))
        }

        if (this.isAdding && this.target.vehicleEquipmentCode) {
            this.loadPreviousDamage(this.target.vehicleEquipmentCode)
        }
    }

    get canEdit(): boolean {
        return this.services.authentication.hasFeature([FeatureType.SafetyVehicleInspectionEdit])
    }

    onVehicleSelected() {
        const vehicle = this.vehicleList.find(v => v.equipmentCode === this.target.vehicleEquipmentCode)
        this.target.vehicleDescription = vehicle ? vehicle.description : ''
        this.target.vehicleMainCategory = vehicle ? vehicle.mainCategory : ''

        const operatorHrRef = vehicle ? vehicle.operatorHrRef ?? null : null
        this.target.vehicleAssignedToHrRef = operatorHrRef
        this.onVehicleAssignedToSelected()

        if (vehicle?.hourReading != null && vehicle.hourReading > 0) {
            this.target.hourReading = vehicle.hourReading
        } else if (this.isAdding) {
            this.target.hourReading = null
        }

        if (this.isAdding && this.target.vehicleEquipmentCode) {
            this.loadPreviousDamage(this.target.vehicleEquipmentCode)
        } else {
            this.previousDamage = null
        }
    }

    get selectedVehicleIsHourly(): boolean {
        const vehicle = this.vehicleList.find(v => v.equipmentCode === this.target.vehicleEquipmentCode)
        return vehicle != null && vehicle.hourReading != null && vehicle.hourReading > 0
    }

    onInspectorSelected() {
        const inspectorId = this.target.inspectorHrRef
        const user = inspectorId == null ? null : this.userMap.find(u => u.value === inspectorId)
        this.target.inspectorName = user ? user.display : ''
    }

    onVehicleAssignedToSelected() {
        const assignedToId = this.target.vehicleAssignedToHrRef
        const user = assignedToId == null ? null : this.userMap.find(u => u.value === assignedToId)
        this.target.vehicleAssignedToName = user ? user.display : ''
    }

    toggleSection(index: number) {
        this.collapsedSections[index] = !this.collapsedSections[index]
    }

    onNotApplicableChanged(sectionIndex: number) {
        if (this.target.sections[sectionIndex].notApplicable) {
            this.collapsedSections[sectionIndex] = true
        }
    }

    isConnexSection(index: number): boolean {
        return this.target.sections[index]?.name === 'Connex Info'
    }

    isTrailerSection(index: number): boolean {
        return this.target.sections[index]?.name === 'Job Trailer Info'
    }

    isDamageItem(item: ViChecklistItem): boolean {
        return item.question === VI_DAMAGE_QUESTION || item.question === VI_LEGACY_DAMAGE_QUESTION
    }

    hasPreviousDamageNotes(): boolean {
        if (!this.previousDamage) return false
        return this.hasPreviousItemNotes(this.previousDamage.damage)
            || this.hasPreviousItemNotes(this.previousDamage.smoke)
            || this.hasPreviousItemNotes(this.previousDamage.newSmoke)
    }

    saveDraft() {
        this.submitOnSave = false
        if (this.target.status !== 'Submitted') {
            this.target.status = 'Draft'
        }
        this.save()
    }

    submit() {
        this.submitOnSave = true
        this.target.status = 'Submitted'
        if (!this.target.signedOn) this.target.signedOn = new Date()
        if (!this.target.submittedOn) this.target.submittedOn = new Date()
        this.save()
    }

    protected override onBeforeSave(): boolean {
        if (!this.target.vehicleEquipmentCode) {
            this.services.notification.warning('Vehicle is required')
            return false
        }
        if (this.submitOnSave) {
            if (this.selectedVehicleIsHourly && (this.target.hourReading == null || this.target.hourReading <= 0)) {
                this.services.notification.warning('Hour reading is required for hourly equipment when submitting.')
                return false
            }
            const missingResponses = this.findItemsMissingResponses()
            if (missingResponses > 0) {
                const proceed = confirm(`${missingResponses} checklist item(s) have no Response selected. Submit anyway?`)
                if (!proceed) return false
            }
        }
        return true
    }

    protected override onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        const observable = this.isAdding || this.isDuplicating ? this.repositoryAdd() : this.repositoryEdit()
        observable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: savedDto => {
                this.uploadPendingPhotos(savedDto).subscribe({
                    next: () =>
                        this.deletePhotosMarkedForRemoval().subscribe({
                            next: () => resolveSave(savedDto),
                            error: e => rejectSave(e),
                        }),
                    error: e => rejectSave(e),
                })
            },
            error: e => rejectSave(e),
        })
    }

    isItemExpanded(itemId: string): boolean {
        return this.expandedItemIds.has(itemId)
    }

    toggleItemPhotos(itemId: string) {
        if (this.expandedItemIds.has(itemId)) {
            this.expandedItemIds.delete(itemId)
        } else {
            this.expandedItemIds.add(itemId)
        }
    }

    pendingPhotosFor(itemId: string): PendingPhoto[] {
        return this.pendingUploadsByItemId.get(itemId) ?? []
    }

    visibleExistingFor(item: ViChecklistItem): string[] {
        const removed = this.deletedExistingByItemId.get(item.id)
        if (!removed?.size) return item.imageUrls ?? []
        return (item.imageUrls ?? []).filter(u => !removed.has(u))
    }

    photoCountFor(item: ViChecklistItem): number {
        return this.visibleExistingFor(item).length + this.pendingPhotosFor(item.id).length
    }

    async onItemPhotoChanged(item: ViChecklistItem, event: Event) {
        const input = event.target as HTMLInputElement
        if (!input.files?.length) return
        await this.addPhotosFromFiles(item.id, input.files)
        input.value = ''
    }

    onItemPhotoDrop(item: ViChecklistItem, event: DragEvent) {
        event.preventDefault()
        event.stopPropagation()
        this.dragOverItemId = null
        const files = event.dataTransfer?.files
        if (files?.length) void this.addPhotosFromFiles(item.id, files)
    }

    onItemPhotoDragOver(item: ViChecklistItem, event: DragEvent) {
        event.preventDefault()
        event.stopPropagation()
        this.dragOverItemId = item.id
    }

    onItemPhotoDragLeave(event: DragEvent) {
        event.preventDefault()
        event.stopPropagation()
        this.dragOverItemId = null
    }

    removePendingPhoto(itemId: string, photo: PendingPhoto) {
        const list = this.pendingUploadsByItemId.get(itemId)
        if (!list) return
        const idx = list.indexOf(photo)
        if (idx >= 0) list.splice(idx, 1)
    }

    removeExistingPhoto(item: ViChecklistItem, url: string) {
        let removed = this.deletedExistingByItemId.get(item.id)
        if (!removed) {
            removed = new Set<string>()
            this.deletedExistingByItemId.set(item.id, removed)
        }
        removed.add(url)
    }

    existingPhotoSrc(url: string): string {
        return './Resources/Images/VehicleInspectionItem/' + url + '?w=300'
    }

    openExistingPhoto(url: string) {
        window.open('./Resources/Images/VehicleInspectionItem/' + url, '_blank')
    }

    isDragOverItem(itemId: string): boolean {
        return this.dragOverItemId === itemId
    }

    private loadPreviousDamage(vehicleEquipmentCode: string) {
        this.previousDamageLoading = true
        const params = new HttpParams().set('vehicleEquipmentCode', vehicleEquipmentCode)
        this.services.data
            .get<PreviousDamageResponse>('api/VehicleInspection/GetPreviousDamage', params)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    this.previousDamage = result
                    this.previousDamageLoading = false
                },
                error: () => {
                    this.previousDamage = null
                    this.previousDamageLoading = false
                },
            })
    }

    private hasPreviousItemNotes(item: PreviousDamageItem | null | undefined): boolean {
        if (!item) return false
        return !!(item.comments?.trim() || item.response?.trim())
    }

    private focusChecklistItemById(itemId: string) {
        for (let i = 0; i < this.target.sections.length; i++) {
            const sec = this.target.sections[i]
            if (sec.items.some(it => String(it.id).toLowerCase() === String(itemId).toLowerCase())) {
                this.collapsedSections[i] = false
                break
            }
        }
        setTimeout(() => {
            document.getElementById('vi-item-' + itemId.toLowerCase())?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 0)
    }

    private findItemsMissingResponses(): number {
        let count = 0
        for (const section of this.target.sections) {
            if (section.notApplicable) continue
            for (const item of section.items) {
                if (!item.response) count++
            }
        }
        return count
    }

    private async addPhotosFromFiles(itemId: string, files: FileList) {
        const list = this.pendingUploadsByItemId.get(itemId) ?? []
        const options: Options = { maxSizeMB: 2, maxWidthOrHeight: 1920, useWebWorker: true }
        for (let i = 0; i < files.length; i++) {
            const file = files[i]
            if (!file.type.startsWith('image/')) continue
            try {
                const compressed = await imageCompression(file, options)
                const url = await imageCompression.getDataUrlFromFile(compressed)
                list.push({ file: compressed, url, type: compressed.type || file.type })
            } catch {
                list.push({ file, url: '', type: file.type })
            }
        }
        if (list.length) {
            this.pendingUploadsByItemId.set(itemId, list)
            this.expandedItemIds.add(itemId)
        }
    }

    private uploadPendingPhotos(savedDto: any): Observable<unknown> {
        const calls: Observable<unknown>[] = []
        const serverItemIds = this.collectServerItemIds(savedDto)
        for (const [itemId, pending] of this.pendingUploadsByItemId.entries()) {
            if (!pending.length) continue
            if (!serverItemIds.has(itemId)) continue
            const formData = new FormData()
            formData.append('id', itemId)
            pending.forEach((p, i) => formData.append('myFile' + i, p.file, p.file.name))
            calls.push(this.services.data.post('api/VehicleInspection/UploadImages', formData))
        }
        return calls.length === 0 ? of(null) : forkJoin(calls)
    }

    private deletePhotosMarkedForRemoval(): Observable<unknown> {
        const calls: Observable<unknown>[] = []
        for (const [, removed] of this.deletedExistingByItemId.entries()) {
            for (const url of removed) {
                const imageId = VehicleInspectionModalComponent.imageIdFromUrl(url)
                if (!imageId) continue
                calls.push(
                    this.services.data.http.delete(
                        `api/VehicleInspection/DeleteImage?imageId=${encodeURIComponent(imageId)}`,
                    ),
                )
            }
        }
        return calls.length === 0 ? of(null) : forkJoin(calls)
    }

    private collectServerItemIds(savedDto: any): Set<string> {
        const ids = new Set<string>()
        for (const s of savedDto?.sections ?? []) {
            for (const i of s.items ?? []) {
                if (i?.id) ids.add(i.id)
            }
        }
        return ids
    }

    private static imageIdFromUrl(url: string): string | null {
        const parts = url.split('/').filter(Boolean)
        if (parts.length < 2) return null
        const file = parts[1]
        const dot = file.lastIndexOf('.')
        const idPart = dot > 0 ? file.slice(0, dot) : file
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idPart) ? idPart : null
    }
}
